import { Flex } from "@chakra-ui/react";
import { RootState, useAppDispatch, useAppSelector } from "@/store";
import { useEffect, useState } from "react";
import { transformGuides } from "@/helpers/transformGuides";
import { FormikProps } from "formik";
import { VacancyForm } from "@/model/vacancy";
import { clearCurrentProject, fetchSingleProject } from "@/store/slices/projects";
import { fetchAllProjects } from "@/store/slices/projects";
import FileUploader from "@/components/FileUploader/FileUploader";
import { BaseInput } from "@/components/Controls/BaseInput";
import { BaseTextArea } from "@/components/Controls/BaseTextArea";
import { FinancialInfoBlock } from "@/components/Controls/FinancialInfoBlock";
import { FormWrapper } from "@/components/FormWrapper/FormWrapper";
import { Customer } from "@/model/customers";
import { BaseSelect, SelectOption } from "@/components/Controls/BaseSelect";
import { fetchSingleApplication } from "@/store/slices/applications";
import { fetchRecruiters } from "@/store/slices/vacancy";
import { CreatableProfileSelect } from "@/components/Controls/CreatableProfileSelect";

interface UpdateVacancyBlockProps {
  formik: FormikProps<VacancyForm>;
  setIsChangedApplication?: (arg: boolean) => void;
}

export const UpdateVacancyBlock = ({ formik, setIsChangedApplication }: UpdateVacancyBlockProps) => {
  const { currentVacancy, recruiters } = useAppSelector((state: RootState) => state.vacancy);
  const { guides } = useAppSelector((state: RootState) => state.guides);
  const dispatch = useAppDispatch();
  const { projects, currentProject } = useAppSelector((state: RootState) => state.projects);
  const { customers } = useAppSelector((state: RootState) => state.customers);

  const projectsOptions = projects.items.map((el) => ({
    value: el.id,
    label: el.name,
  }));

  useEffect(() => {
    if (formik.values.customer_id !== null && formik.values.customer_id !== undefined) {
      dispatch(fetchAllProjects({ params: { filters: { customer_id: formik.values.customer_id.toString() } } }));
    }
  }, [dispatch, formik.values.customer_id]);

  useEffect(() => {
    if (currentProject) {
      formik.setValues({
        ...formik.values,
        customer_id: currentProject.customer?.id ?? 0,
        project_description: currentProject.description ?? "",
        legal_entity_id: currentProject.legal_entity?.id ?? 0
      });
    }
  }, [currentProject]);

  const [jobFormatOptions, setJobFormatOptions] = useState<SelectOption[]>([]);
  const [jobWorkloadOptions, setJobWorkloadOptions] = useState<SelectOption[]>([]);
  const [attractionPeriodOptions, setAttractionPeriodOptions] = useState<SelectOption[]>([]);
  const [gradeOptions, setGradeOptions] = useState<SelectOption[]>([]);
  const [legalEntityOptions, setLegalEntityOptions] = useState<SelectOption[]>([]);
  const [profileOptions, setProfileOptions] = useState<SelectOption[]>([]);
  const [currentFiles, setCurrentFiles] = useState<Array<{ name: string; id: string; isCurrentFile: boolean }>>([]);

  const customerOptions: SelectOption[] = customers.items.map((el: Customer) => {
    const value = el.id !== undefined && typeof el.id === "number" ? el.id : undefined;
    const label = el.name !== undefined && typeof el.name === "string" ? el.name : undefined;

    return {
      value: value,
      label: label || "",
    };
  });

  useEffect(() => {
    if (guides) {
      const resultJobFormatList = transformGuides(guides.JobFormat).map((o: { value: string | number; label?: string }) => ({
        value: String(o.value),
        label: String(o.label || ""),
      }));
      setJobFormatOptions(resultJobFormatList);

      const resultJobWorkloadList = transformGuides(guides.JobWorkload).map((o: { value: string | number; label?: string }) => ({
        value: String(o.value),
        label: String(o.label || ""),
      }));
      setJobWorkloadOptions(resultJobWorkloadList);

      const resultAttractionPeriodList = transformGuides(guides.AttractionPeriod).map((o: { value: string | number; label?: string }) => ({
        value: String(o.value),
        label: String(o.label || ""),
      }));
      setAttractionPeriodOptions(resultAttractionPeriodList);

      const grades = transformGuides(guides.Grade).map((o: { value: string | number; label?: string }) => ({
        value: String(o.value),
        label: String(o.label || ""),
      }));
      setGradeOptions(grades);

      const resultLegalEntityList = transformGuides(guides.LegalEntity).map((o: { value: string | number; label?: string }) => ({
        value: Number(o.value),
        label: String(o.label || ""),
      }));
      setLegalEntityOptions(resultLegalEntityList);

      const resultProfileList = transformGuides(guides.ApplicationProfile).map((o: { value: string | number; label?: string }) => ({
        value: Number(o.value),
        label: String(o.label || ""),
      }));
      setProfileOptions(resultProfileList);
    }
  }, [guides]);

  useEffect(() => {
    const resultFiles =
      currentVacancy?.attachments &&
      Object.entries(currentVacancy.attachments).map(([key, el]) => {
        return {
          name: (el as { name: string }).name,
          id: key,
          isCurrentFile: true,
        };
      });
    setCurrentFiles(resultFiles || []);
  }, [currentVacancy]);

  useEffect(() => {
    currentVacancy && dispatch(fetchSingleApplication({ params: { id: currentVacancy?.application.id } }));
  }, [currentVacancy, dispatch]);

  useEffect(() => {
    dispatch(fetchRecruiters({ appointed_recruiter: true, recruiter: false }));
  }, [dispatch]);

  const recruiterOptions: SelectOption[] = recruiters.map((recruiter) => ({
    value: Number(recruiter.id),
    label: recruiter.username,
  }));

  return (
    <FormWrapper>
      <Flex gap="48px" paddingBottom="20px">
        <BaseInput
          fieldTitle={"Название должности"}
          requiredField={true}
          value={formik.values.name}
          onChange={formik.handleChange}
          name="name"
          placeholder="Введите название вакансии"
          containerStyles={{ width: "100%" }}
        />
        <BaseSelect
          fieldTitle="Заказчик"
          requiredField
          options={customerOptions}
          placeholder="Выберите заказчика"
          value={formik.values.customer_id}
          onChange={(value) => {
            const nextCustomerId = value ? Number(value) : 0;
            dispatch(clearCurrentProject());
            formik.setFieldValue("customer_id", nextCustomerId);
            formik.setFieldValue("project_id", 0);
            formik.setFieldValue("project_description", "");
            formik.setFieldValue("legal_entity_id", 0);
          }}
          containerStyles={{ width: "100%" }}
          isEmpty={formik.values.customer_id === 0}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Проект"
          options={projectsOptions}
          placeholder="Выберите проект"
          key={`project-select-${formik.values.customer_id ?? "none"}`}
          value={formik.values.project_id}
          onChange={(value) => {
            const id = value ? Number(value) : 0;
            if (id === 0) {
              formik.setFieldValue("project_description", "");
            } else {
              dispatch(fetchSingleProject({ id }));
            }
            formik.setFieldValue("project_id", id);
          }}
          containerStyles={{ width: "100%" }}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Юр. лицо"
          options={legalEntityOptions}
          placeholder="Выберите юр. лицо"
          requiredField={true}
          value={formik.values.legal_entity_id}
          onChange={(value) => formik.setFieldValue("legal_entity_id", value ? Number(value) : 0)}
          containerStyles={{ width: "100%", minWidth: "300px" }}
        />
        <CreatableProfileSelect
          fieldTitle="Профиль"
          requiredField
          options={profileOptions}
          placeholder="Выберите профиль или начните вводить для поиска"
          value={formik.values.profile_id}
          onChange={(value) => formik.setFieldValue("profile_id", value ? Number(value) : null)}
          containerStyles={{ width: "100%", minWidth: "300px" }}
          isEmpty={!formik.values.profile_id}
        />
        <BaseSelect
          fieldTitle="Грейд"
          requiredField
          options={gradeOptions}
          placeholder="Выберите грейд"
          value={formik.values.candidate_grade}
          onChange={(value) => formik.setFieldValue("candidate_grade", Array.isArray(value) ? value : [])}
          containerStyles={{ width: "100%", minWidth: "300px" }}
          isMulti
          isEmpty={!formik.values.candidate_grade || formik.values.candidate_grade.length === 0}
        />
      </Flex>

      <Flex paddingBottom="20px">
        <BaseTextArea
          containerStyles={{ width: "100%" }}
          fieldTitle={"Требования к кандидату"}
          requiredField={true}
          id="candidate_requirements"
          value={formik.values.candidate_requirements}
          onChange={formik.handleChange}
          placeholder="Опишите требования к кандидату"
        />
      </Flex>

      <Flex gap="20px" paddingBottom="20px" flexDirection="column">
        <BaseTextArea
          fieldTitle={"Обязанности кандидата"}
          id="job_duties"
          requiredField={true}
          value={formik.values.job_duties}
          onChange={formik.handleChange}
          placeholder="Опишите обязанности кандидата"
        />

        <BaseTextArea
          fieldTitle="Описание проекта"
          name={"project_description"}
          onChange={formik.handleChange}
          value={formik.values.project_description}
          placeholder="Краткое описание проекта"
          requiredField
        />

        <FinancialInfoBlock
          valueGeneral={formik.values.sales_max_offer_general || ""}
          valueMoscow={formik.values.sales_max_offer_moscow || ""}
          valueRegion={formik.values.sales_max_offer_regions || ""}
          onChangeGeneral={(value) => formik.setFieldValue("sales_max_offer_general", value)}
          onChangeMoscow={(value) => formik.setFieldValue("sales_max_offer_moscow", value)}
          onChangeRegion={(value) => formik.setFieldValue("sales_max_offer_regions", value)}
          valueSalaryTk={String(formik.values.salary_tk_max || "")}
          valueSalaryIp={String(formik.values.salary_ip_max || "")}
          valueSalaryGph={String(formik.values.salary_gph_max || "")}
          onChangeSalaryTk={(value) => formik.setFieldValue("salary_tk_max", Number(value))}
          onChangeSalaryIp={(value) => formik.setFieldValue("salary_ip_max", Number(value))}
          onChangeSalaryGph={(value) => formik.setFieldValue("salary_gph_max", Number(value))}
        />
      </Flex>

      <Flex gap="48px" paddingTop="20px" paddingBottom="20px">
        <BaseInput
          fieldTitle="Маржинальность"
          onChange={formik.handleChange}
          value={`${formik.values.marginality ?? ""}`}
          name={"marginality"}
          placeholder="Введите маржинальность"
          containerStyles={{ flex: 1 }}
          requiredField={true}
        />
        <BaseInput
          fieldTitle="Ставка закупки (max)"
          onChange={formik.handleChange}
          value={formik.values.purchase_rate_max || ""}
          name={"purchase_rate_max"}
          placeholder="Введите ставку закупки"
          containerStyles={{ flex: 1 }}
        />
        <BaseInput
          fieldTitle="Сколько человек нужно нанять"
          value={`${formik.values.required_number ?? ""}`}
          onChange={formik.handleChange}
          name="required_number"
          requiredField={true}
          placeholder="Введите количество человек"
          containerStyles={{ flex: 1 }}
        />
      </Flex>

      <Flex paddingBottom="20px">
        <BaseSelect
          fieldTitle="Назначенные рекрутеры"
          requiredField
          options={recruiterOptions}
          placeholder="Выберите рекрутеров"
          value={formik.values.recruiters_ids}
          onChange={(value) => formik.setFieldValue("recruiters_ids", Array.isArray(value) ? value.map(Number) : [])}
          containerStyles={{ width: "100%", minWidth: "300px" }}
          isMulti
          isEmpty={!formik.values.recruiters_ids || formik.values.recruiters_ids.length === 0}
        />
      </Flex>

      <Flex gap="48px" paddingBottom="20px">
        <BaseInput
          fieldTitle="Локация поиска"
          value={formik.values.location}
          onChange={formik.handleChange}
          name="location"
          placeholder="Город/регион"
          containerStyles={{ flex: 1 }}
        />
        <BaseSelect
          fieldTitle="Формат работы"
          options={jobFormatOptions}
          placeholder="Выберите формат"
          value={formik.values.job_format}
          onChange={(value) => formik.setFieldValue("job_format", value)}
          containerStyles={{ flex: 1 }}
          requiredField
        />
      </Flex>

      <BaseTextArea
        fieldTitle="Адрес офиса"
        name={"office_address"}
        onChange={formik.handleChange}
        value={formik.values.office_address}
        placeholder="Укажите адрес офиса (если есть)"
      />

      <Flex gap="48px" paddingTop="20px" paddingBottom="20px">
        <BaseSelect
          fieldTitle="Занятость"
          options={jobWorkloadOptions}
          placeholder="Выберите занятость"
          value={formik.values.job_workload}
          onChange={(value) => formik.setFieldValue("job_workload", value)}
          requiredField={true}
          containerStyles={{ width: "100%" }}
        />
        <BaseSelect
          fieldTitle="Срок привлечения"
          options={attractionPeriodOptions}
          placeholder="Выберите срок"
          value={formik.values.attraction_period}
          onChange={(value) => formik.setFieldValue("attraction_period", value)}
          containerStyles={{ width: "100%" }}
        />
      </Flex>

      <BaseTextArea
        fieldTitle={"Комментарии к вакансии"}
        id="comments"
        value={formik.values.comments}
        onChange={formik.handleChange}
        placeholder="Добавьте комментарий"
      />

      <Flex className={"form-column"} flex="1" paddingTop="20px">
        <FileUploader
          onChangeDeletedIds={(ids) => formik.setFieldValue("attachments_delete", ids)}
          onChange={(files) => formik.setFieldValue("attachments", files)}
          currentFiles={currentFiles}
        />
      </Flex>
    </FormWrapper>
  );
};
